<template>
  <div class="w-full px-6 py-4">
    <o-table
      v-if="_service"
      class="w-full"
      container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
      :data="_service.automations"
      :headers="[
        {text: $t('service.automations')},
        {text: $t('general.frequency')},
        {text: $t('general.mode')},
        {text: $t('general.actions')}
      ]"
      :limit="20"

      :title="$t('components.service.edit.automations.title')"
      striped
    >
      <template #controls>
        <o-button variant="primary" @click="handleAddServiceAutomation">
          {{ $t('components.service.edit.automations.add') }}
        </o-button>
      </template>

      <template #row="{ row, tdClass }">
        <td :class="tdClass">
          <div class="flex items-center space-x-2">
            <o-tooltip :text="row.enabled ? $t('general.enabled') : $t('general.disabled')">
              <div
                :class="[
                  'w-2 h-2 rounded-full',
                  row.enabled ? 'bg-green-500' : 'bg-red-500',
                ]"
              />
            </o-tooltip>
            <p>{{ getTypeLabel(row) }} - {{ row.name }}</p>
          </div>
        </td>
        <td :class="tdClass">
          {{ getFrequencyLabel(row) }}
        </td>
        <td :class="tdClass">
          {{ getRequirementModeLabel(row) }}

          <span v-if="row.isRequirementModeScheduled">
            ({{ formatScheduleOffset(row.scheduleOffset) }})
          </span>
        </td>
        <td :class="tdClass">
          <div class="flex items-center space-x-1">
            <o-tooltip :text="$t('general.edit')">
              <o-button icon="edit" size="sm" @click="handleEditServiceAutomation(row)" />
            </o-tooltip>
            <o-tooltip :text="$t('general.delete')">
              <o-button icon="delete" variant="danger" size="sm" flat @click="handleRemoveServiceAutomation(row)" />
            </o-tooltip>
          </div>
        </td>
      </template>
    </o-table>

    <AddServiceAutomationModal
      ref="addServiceAutomationModal"
      :service="_service"
      @success="handleSuccess"
    />

    <EditServiceAutomationModal
      ref="editServiceAutomationModal"
      :service="_service"
      @success="handleSuccess"
    />

    <RemoveServiceAutomationModal
      ref="removeServiceAutomationModal"
      :service="_service"
      @success="handleSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { formatDuration } from 'date-fns'

import AddServiceAutomationModal from '@/components/service/actions/AddAutomation.vue'
import EditServiceAutomationModal from '@/components/service/actions/EditAutomation.vue'
import RemoveServiceAutomationModal from '@/components/service/actions/RemoveAutomation.vue'
import { OTable } from '@/components/elements'

import {
  ServiceAutomationTypeLabels,
  ServiceAutomationFrequencyLabels,
  ServiceAutomationRequirementModeLabels,
} from '~/_api/models/serviceAutomation'
import type { Service } from '~/_api/models/service'
import type { ServiceAutomation } from '~/_api/models/serviceAutomation'

const props = defineProps<{
  service: Service
}>()

const emit = defineEmits<{
  (e: 'update:service', service: Service): void
  (e: 'update'): void
}>()

const { $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

// Template refs
const addServiceAutomationModal = ref()
const editServiceAutomationModal = ref()
const removeServiceAutomationModal = ref()

const _service = computed({
  get: () => props.service,
  set: (value) => emit('update:service', value),
})

const getTypeLabel = (automation: ServiceAutomation): string => {
  return $t(ServiceAutomationTypeLabels[automation.type] || 'general.unknown')
}

const getFrequencyLabel = (automation: ServiceAutomation): string => {
  return $t(ServiceAutomationFrequencyLabels[automation.frequency] || 'general.unknown')
}

const getRequirementModeLabel = (automation: ServiceAutomation): string => {
  return $t(ServiceAutomationRequirementModeLabels[automation.requirementMode] || 'general.unknown')
}

const formatScheduleOffset = (offset: number): string => {
  const hours = Math.abs(offset)

  const duration = formatDuration({
    hours,
  })

  return offset < 0
    ? $t('components.service.edit.automations.timeBefore', { duration })
    : $t('components.service.edit.automations.timeAfter', { duration })
}

const handleAddServiceAutomation = (): void => {
  addServiceAutomationModal.value?.open()
}

const handleEditServiceAutomation = (serviceAutomation: ServiceAutomation): void => {
  editServiceAutomationModal.value?.open(serviceAutomation)
}

const handleRemoveServiceAutomation = (serviceAutomation: ServiceAutomation): void => {
  removeServiceAutomationModal.value?.open(serviceAutomation)
}

const handleSuccess = (): void => {
  emit('update')
}
</script>
